<template>
  <CategoryFilterWrapper>
    <template #drawerButton>
      <c-box fontSize="sm" lineHeight="1.2" fonwWeight="400" maxWidth="80vw">
        <template v-if="selectedCategory.parent">
          <c-flex>
            <c-icon name="chevron_left" color="brand.purple" size="1rem" />
            <c-box
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              pl="sm"
              ml="sm"
              borderLeft="1px solid var(--chakra-colors-gray-500)"
              >{{ selectedCategory.label }}</c-box
            >
          </c-flex>
        </template>
        <template v-else>
          <c-flex>
            <c-icon
              name="sub_navigation"
              color="brand.purple"
              size="1.5rem"
              mt="-0.25rem"
              mb="-0.25rem"
            />
            <c-box ml="sm">{{ $t("productSearch.categoryFilter.allProducts") }}</c-box>
          </c-flex>
        </template>
      </c-box>
    </template>
    <template #default>
      <div
        v-if="
          selectedCategory.children || (selectedCategory.parent && selectedCategory.parent.parent)
        "
      >
        <template v-if="selectedCategory.children && selectedCategory.children.length">
          <!-- Selected category is not a leaf in the category tree -->
          <c-flex
            v-if="selectedCategory.parent"
            :title="getBreadcrumbTooltip(selectedCategory)"
            mb="3xl"
          >
            <c-box
              cursor="pointer"
              pr="xs"
              @click="select(selectedCategory.parent)"
              tabindex="0"
              @keydown.enter="select(selectedCategory.parent)"
            >
              <c-icon name="chevron_left" color="brand.purple" size="1rem" />
            </c-box>
            <c-box
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              pl="sm"
              borderLeft="1px solid var(--chakra-colors-brand-grayDark)"
              color="brand.purple"
            >
              {{ selectedCategory.label }}
            </c-box>
          </c-flex>
          <c-box
            color="gray.700"
            ml="xs"
            v-chakra="{ textIndent: 'calc(-1.0 * var(--chakra-space-xs))' }"
          >
            <c-box
              v-for="child in selectedCategory.children"
              v-bind:key="child.id"
              :color="getColor(child)"
              cursor="pointer"
              @click="select(child)"
              tabindex="0"
              @keydown.enter="select(child)"
              mb="md"
            >
              {{ child.label }}
            </c-box>
          </c-box>
        </template>
        <template v-else>
          <!-- Selected category is a leaf in the category tree -->
          <c-flex :title="getBreadcrumbTooltip(selectedCategory.parent)" mb="3xl">
            <!-- Assumption: leaf categories have at least 2 parents (when the root is counted) -->
            <c-box
              pr="xs"
              :color="getColor(selectedCategory.parent?.parent)"
              cursor="pointer"
              @click="select(selectedCategory.parent?.parent)"
              tabindex="0"
              @keydown.enter="select(selectedCategory.parent?.parent)"
            >
              <c-icon name="chevron_left" color="brand.purple" size="1rem" />
            </c-box>
            <c-box>
              <!-- For some insane reason the @click handler below is not working without an extra element here -->
            </c-box>
            <c-box
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              pl="sm"
              borderLeft="1px solid var(--chakra-colors-brand-grayDark)"
              :color="getColor(selectedCategory.parent)"
              cursor="pointer"
              @click="select(selectedCategory.parent)"
              tabindex="0"
              @keydown.enter="select(selectedCategory.parent)"
            >
              {{ selectedCategory.parent?.label }}
            </c-box>
          </c-flex>
          <c-box
            color="gray.700"
            ml="xs"
            v-chakra="{ textIndent: 'calc(-1.0 * var(--chakra-space-xs))' }"
          >
            <c-box
              v-for="child in selectedCategory.parent?.children"
              v-bind:key="child.id"
              :color="getColor(child)"
              cursor="pointer"
              @click="select(child)"
              tabindex="0"
              @keydown.enter="select(child)"
              mb="md"
            >
              <c-icon
                name="check"
                color="brand.purple"
                mt="-5px"
                mr="2px"
                size="1rem"
                :d="child.id === selectedCategory.id ? 'inline-block' : 'none'"
              />
              {{ child.label }}
            </c-box>
          </c-box>
        </template>
      </div>
    </template>
  </CategoryFilterWrapper>
</template>

<script lang="ts">
import CategoryFilterWrapper from "@/components/products/CategoryFilterWrapper.vue";
import { IUiProductCategory } from "@/store/productSearchModule";
import { CBox, CFlex, CIcon } from "@chakra-ui/vue";
import _ from "lodash";
import { defineComponent } from "vue";
import { mapState } from "vuex";

export default defineComponent({
  name: "CategoryFilter",
  components: {
    CategoryFilterWrapper,
    CIcon,
    CBox,
    CFlex,
  },
  computed: {
    ...mapState("productSearch", {
      storeSelectedCategory: "selectedCategory",
    }),
  },
  data() {
    return {
      selectedCategory: { id: "", label: "" } as IUiProductCategory,
    };
  },
  beforeMount() {
    this.selectedCategory = _.cloneDeep(this.storeSelectedCategory);
  },
  watch: {
    storeSelectedCategory: {
      deep: true,
      handler() {
        if (!_.isEqual(this.storeSelectedCategory, this.selectedCategory)) {
          this.selectedCategory = _.cloneDeep(this.storeSelectedCategory);
        }
      },
    },
  },
  methods: {
    select(item) {
      if (this.selectedCategory !== item) {
        this.selectedCategory = item;
      } else {
        // Deselect child = select parent
        this.selectedCategory = item.parent;
      }
      this.$store.dispatch("productSearch/updateSelectedCategory", this.selectedCategory);
    },
    getBreadcrumbTooltip(lastBreadcrumbCategory) {
      let result = lastBreadcrumbCategory.label;
      let category = lastBreadcrumbCategory.parent;
      while (category.parent) {
        result = `${category.label} | ${result}`;
        category = category.parent;
      }
      return result;
    },
    getColor(item) {
      if (!item.matching) {
        return "brand.grayDark";
      } else if (item.id === this.selectedCategory.id) {
        return "brand.purple";
      }
      return "gray.700";
    },
  },
});
</script>
